import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    materialApi,
    type Material,
    type MaterialFilters,
    type CreateMaterialData,
    type UpdateMaterialData,
} from "@/api/material";

export const useMaterialStore = defineStore("material", () => {
    // State
    const materials = ref<Material[]>([]);
    const currentMaterial = ref<Material | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const classSubjectId = ref("");
    const teacherId = ref("");
    const classId = ref("");
    const type = ref("");
    const isPublished = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const publishedMaterials = computed(() =>
        materials.value.filter((material) => material.is_published),
    );

    const unpublishedMaterials = computed(() =>
        materials.value.filter((material) => !material.is_published),
    );

    const materialsByType = computed(() => {
        const grouped: Record<string, Material[]> = {};
        materials.value.forEach((material) => {
            const typeName =
                material.type_display || material.type || "Unknown Type";
            if (!grouped[typeName]) grouped[typeName] = [];
            grouped[typeName].push(material);
        });
        return grouped;
    });

    const materialsByClassSubject = computed(() => {
        const grouped: Record<string, Material[]> = {};
        materials.value.forEach((material) => {
            const classSubjectName =
                material.class_subject?.class?.grade_level +
                    " " +
                    material.class_subject?.class?.name +
                    " - " +
                    material.class_subject?.subject?.name ||
                "Unknown Class Subject";
            if (!grouped[classSubjectName]) grouped[classSubjectName] = [];
            grouped[classSubjectName].push(material);
        });
        return grouped;
    });

    // Actions
    const fetchMaterials = async (filters: MaterialFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await materialApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                class_subject_id: classSubjectId.value || undefined,
                teacher_id: teacherId.value || undefined,
                class_id: classId.value || undefined,
                type: type.value || undefined,
                is_published:
                    isPublished.value !== ""
                        ? isPublished.value === "true"
                        : undefined,
                ...filters,
            });

            materials.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch materials";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchMaterial = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentMaterial.value = (await materialApi.getById(id)).data;
            return currentMaterial.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch material";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createMaterial = async (data: CreateMaterialData) => {
        loading.value = true;
        error.value = null;

        try {
            const newMaterial = await materialApi.create(data);
            // Refresh list to ensure consistency
            await fetchMaterials();
            return newMaterial;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create material";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateMaterial = async (id: string, data: UpdateMaterialData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedMaterial = await materialApi.update(id, data);
            const index = materials.value.findIndex((m) => m.id === id);
            if (index !== -1) {
                materials.value[index] = updatedMaterial.data;
            }
            if (currentMaterial.value?.id === id) {
                currentMaterial.value = updatedMaterial.data;
            }
            return updatedMaterial;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update material";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteMaterial = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await materialApi.delete(id);
            materials.value = materials.value.filter((m) => m.id !== id);
            if (currentMaterial.value?.id === id) {
                currentMaterial.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete material";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const publishMaterial = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedMaterial = await materialApi.publish(id);
            const index = materials.value.findIndex((m) => m.id === id);
            if (index !== -1) {
                materials.value[index] = updatedMaterial;
            }
            if (currentMaterial.value?.id === id) {
                currentMaterial.value = updatedMaterial;
            }
            return updatedMaterial;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to publish material";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const unpublishMaterial = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedMaterial = await materialApi.unpublish(id);
            const index = materials.value.findIndex((m) => m.id === id);
            if (index !== -1) {
                materials.value[index] = updatedMaterial;
            }
            if (currentMaterial.value?.id === id) {
                currentMaterial.value = updatedMaterial;
            }
            return updatedMaterial;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to unpublish material";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchMaterialsByClassSubject = async (
        classSubjectIdValue: string,
    ) => {
        loading.value = true;
        error.value = null;

        try {
            const materialsByClassSubject =
                await materialApi.getByClassSubject(classSubjectIdValue);
            return materialsByClassSubject;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch materials";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchMaterialsByTeacher = async (teacherIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const materialsByTeacher =
                await materialApi.getByTeacher(teacherIdValue);
            return materialsByTeacher;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch materials";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchMaterialsByClass = async (classIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const materialsByClass = await materialApi.getByClass(classIdValue);
            return materialsByClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch materials";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchMaterialsByType = async (typeValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const materialsByType = await materialApi.getByType(typeValue);
            return materialsByType;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch materials";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
    };

    const resetFilters = async () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        classSubjectId.value = "";
        teacherId.value = "";
        classId.value = "";
        type.value = "";
        isPublished.value = "";
        await fetchMaterials();
    };

    return {
        // State
        materials,
        currentMaterial,
        loading,
        error,
        page,
        perPage,
        search,
        classSubjectId,
        teacherId,
        classId,
        type,
        isPublished,
        meta,

        // Getters
        publishedMaterials,
        unpublishedMaterials,
        materialsByType,
        materialsByClassSubject,

        // Actions
        fetchMaterials,
        fetchMaterial,
        createMaterial,
        updateMaterial,
        deleteMaterial,
        publishMaterial,
        unpublishMaterial,
        fetchMaterialsByClassSubject,
        fetchMaterialsByTeacher,
        fetchMaterialsByClass,
        fetchMaterialsByType,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
